import { alpha } from '@mui/material/styles';

// WhatsApp brand colours
export const WA_GREEN = '#075E54';
export const WA_LIGHT_GREEN = '#25D366';
export const WA_BG = '#ECE5DD';
export const WA_BUBBLE = '#FFFFFF';

// Escape a string for safe insertion into HTML content
export const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

// Parse WhatsApp markup (* bold *, _italic_, ~strikethrough~, ```code```) to HTML
export const parseWhatsappMarkup = (text) => {
    if (!text) return '';
    const tokens = text.split(/({{.*?}})/g);
    return tokens
        .map((token) => {
            if (token.startsWith("{{") && token.endsWith("}}")) {
                return token;
            }
            return token
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/\*(.*?)\*/g, "<b>$1</b>")
                .replace(/_(.*?)_/g, "<i>$1</i>")
                .replace(/~(.*?)~/g, "<s>$1</s>")
                .replace(/```(.*?)```/gs, "<code>$1</code>")
                .replace(/\n/g, "<br>");
        })
        .join('');
};

// Extract {{1}}, {{2}}, ... variable numbers from template text
export const extractVariables = (text) => {
    if (!text) return [];
    const regex = /{{(\d+)}}/g;
    const matches = [];
    let match = regex.exec(text);
    while (match !== null) {
        if (!matches.includes(match[1])) {
            matches.push(match[1]);
        }
        match = regex.exec(text);
    }
    return matches.sort((a, b) => Number(a) - Number(b));
};

// Render text with variables replaced by highlighted spans
export const renderTextWithVariables = (text, contextMapping = {}) => {
    if (!text) return '';
    let parsed = parseWhatsappMarkup(text);
    parsed = parsed.replace(/{{(\d+)}}/g, (match, num) => {
        const mappedValue = contextMapping[num];
        const displayValue = escapeHtml(mappedValue) || `var${num}`;
        return `<span class="template-variable">${displayValue}</span>`;
    });
    return parsed;
};

// Returns bg/color pair for a usage type chip
export const getUsageTypeColor = (usageType, theme) => {
    const colors = {
        'FOLLOW_UP': { bg: alpha(theme.palette.info.main, 0.12), color: theme.palette.info.dark },
        'MARKETING': { bg: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.dark },
        'COMMUNICATION': { bg: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark },
        'PROMOTION': { bg: alpha(theme.palette.secondary.main, 0.12), color: theme.palette.secondary.dark },
        'OTHER': { bg: alpha(theme.palette.grey[600], 0.12), color: theme.palette.grey[800] },
        'AUTOMATION': { bg: alpha(theme.palette.secondary.main, 0.12), color: theme.palette.secondary.darker || theme.palette.secondary.dark },
    };
    return colors[usageType] || { bg: alpha(theme.palette.info.lighter || theme.palette.info.main, 0.12), color: theme.palette.info.dark };
};

// "FOLLOW_UP" -> "Follow Up"
export const formatUsageType = (usageType) => {
    if (!usageType) return 'General';
    const labels = {
        FOLLOW_UP: 'Follow-up',
        FORMS: 'Forms',
        FAVOURITES: 'Favourites',
    };
    if (labels[usageType]) return labels[usageType];
    return usageType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// "my_template_name" -> "My Template Name"
export const formatTemplateName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Template field extractors
export const getTemplateComponents = (template) => (
    Array.isArray(template?.components) ? template.components : []
);

export const getTemplateBody = (template) => {
    const bodyComponent = getTemplateComponents(template).find(c => c.type === 'BODY');
    return bodyComponent?.text || '';
};

export const getTemplateHeader = (template) => {
    const headerComponent = getTemplateComponents(template).find(c => c.type === 'HEADER');
    return headerComponent?.text || '';
};

export const getTemplateFooter = (template) => {
    const footerComponent = getTemplateComponents(template).find(c => c.type === 'FOOTER');
    return footerComponent?.text || '';
};

export const getTemplateButtons = (template) => {
    const buttonsComponent = getTemplateComponents(template).find(c => c.type === 'BUTTONS');
    return buttonsComponent?.buttons || [];
};

export const getTemplateVariables = (template) => {
    const allText = [
        getTemplateHeader(template),
        getTemplateBody(template),
        getTemplateFooter(template),
    ].join(' ');
    return extractVariables(allText);
};

// Strip markup and variables to produce a plain-text preview string.
// Handles both positional ``{{N}}`` and named ``{{name}}`` placeholders.
export const stripToPlain = (text) => {
    if (!text) return '';
    return text
        .replace(/\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, '___')
        .replace(/[*_~`]/g, '')
        .trim();
};

// Fuzzy-ish word-based search match
export const matchesSearch = (name, searchText) => {
    if (!name || !searchText) return true;
    const normalizedName = name.toLowerCase().replace(/_/g, ' ');
    const normalizedSearch = searchText.toLowerCase().trim();
    const searchWords = normalizedSearch.split(/\s+/);
    return searchWords.every(word => normalizedName.includes(word));
};
